<template>
  <ContentModules
    v-if="contentModulesStatus === 'success'"
    :content-modules="contentModules"
    :job-data="job"
    :action-bar-config="actionBarConfig"
  />
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useJobPageTemplateData } from "~/composables/useJobPageTemplateData"
import { useJobData } from "~/composables/useJobData"
import { useContentModules } from "~/composables/useContentModules"
import { useJobRouteParams } from "~/composables/useJobRouteParams"
import { useMatomoGroupDimension } from "~/composables/useMatomoGroupDimension"
import { useJobPostingSchema } from "~/composables/useJobPostingSchema"
import { useJobSeoMeta } from "~/composables/useJobSeoMeta"

// Extract job route params
const { jobId } = useJobRouteParams()

// Fetch job data
const { job, jobDataStatus } = await useJobData(jobId)

// Set matomo group_id as dimension 4
useMatomoGroupDimension(job)

// Fetch job page template data (must wait for job data to get template_id)
const templateId = job.value?.template_id ?? null
const { jobPageTemplate } = await useJobPageTemplateData(templateId)

// Extract actionBar config
const actionBarConfig = computed(() => jobPageTemplate.value?.actionBar || {})

// Fetch content modules
const { contentModules, contentModulesStatus } = await useContentModules(jobPageTemplate.value?.id)

// Extract Hero module image for sharing
const heroModule = contentModules.value?.find(module => module.component === "CRHero")
const heroImage = heroModule?.config?.media?.media?.url || null

const { data: jobShop } = useNuxtData("jobShopData")

const { title, description } = useJobSeoMeta(job)

const trackingData = {
  job,
  contentModules,
  jobShop,
}

// Track page only when data is loaded
watchEffect(() => {
  if (
    jobDataStatus.value === "success"
    && contentModulesStatus.value === "success"
    && job.value
    && contentModules.value
  )
    usePageTracking(trackingData)
})

// Add Schema.org structured data & canonical URL
if (job.value) {
  useSchemaOrg([
    defineWebPage({
      "@id": `${job.value.url}#webpage`,
      "url": job.value.url,
      "potentialAction": [],
    }),
  ])
  useJobPostingSchema(job.value, heroImage)

  useHead({
    link: [
      {
        rel: "canonical",
        href: job.value.url,
      },
    ],
  })
}

useServerSeoMeta({
  title,
  description,
  ogTitle: title,
  ogImage: () => heroImage,
})
</script>
